import { Status } from "@redotech/http/semantics";

export class RpcError<Code extends string> extends Error {
  override name = "RpcError";

  constructor(
    readonly status: Status,

    override readonly message: string,
    readonly code?: Code,
  ) {
    super(message);
  }

  static from(error: unknown): RpcError<string> {
    if (error instanceof RpcError) {
      return error;
    }

    const anyError = error as any;

    /**
     * implicitly, AxiosErrors will have error.response.status, error.response.statusText, and error.response.data.message
     * (didn't want to add axios as a dependency for this library)
     **/
    return new RpcError(
      new Status(
        anyError?.response?.status ?? 500,
        anyError?.response?.statusText ?? "Unknown Error",
      ),
      anyError?.response?.data?.message ??
        anyError?.response?.data?.error ??
        anyError?.message ??
        anyError?.toString() ??
        "(No error message provided)",
    );
  }
}

export class ClientError<Code extends string> extends Error {
  override name = "ClientError";

  constructor(
    readonly statusCode: number,

    override readonly message: string,
    readonly code: Code | undefined,
  ) {
    super(message);
  }
}

/**
 * Map a non-ok RPC `Response` to a ClientError — the one shared mapping for
 * every RPC client (modular and rpc-light), so their error shapes can't drift.
 */
export async function clientErrorFromResponse(
  response: Response,
): Promise<ClientError<string>> {
  let message: string | undefined;
  let code: string | undefined;
  if (response.headers.get("Content-Type")?.includes("application/json")) {
    const json: unknown = await response.json();

    if (json && typeof json === "object") {
      const error = "error" in json ? String(json.error) : undefined;
      const m = "message" in json ? String(json.message) : undefined;
      code = "code" in json ? String(json.code) : undefined;
      message = error || m;
    }
  } else {
    message = await response.text();
  }
  return new ClientError(response.status, message ?? "Unknown error", code);
}
